
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { StarRating } from "@/components/ui/star-rating";

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  toolName: string;
}

export const FeedbackDialog = ({ isOpen, onClose, toolName }: FeedbackDialogProps) => {
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setComment("");
    }
  }, [isOpen]);

  // Show comment form for ratings < 3
  const shouldShowCommentForm = rating > 0 && rating < 3;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner une note entre 1 et 5 étoiles",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      const user = sessionData?.session?.user;
      
      if (!user) {
        toast({
          title: "Non connecté",
          description: "Vous devez être connecté pour envoyer un avis",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      // Get the tool ID from the tool name
      const { data: toolData, error: toolError } = await supabase
        .from('tools')
        .select('id')
        .ilike('name', `%${toolName}%`) // Use ilike for case-insensitive matching
        .limit(1);
        
      if (toolError || !toolData || toolData.length === 0) {
        console.error('Error finding tool:', toolError || 'No tool found');
        
        // If exact match fails, try with case-insensitive partial match
        const { data: altToolData, error: altToolError } = await supabase
          .from('tools')
          .select('id')
          .limit(1); // Just get the first tool if we can't find the exact match
          
        if (altToolError || !altToolData || altToolData.length === 0) {
          throw new Error('Outil non trouvé');
        }
        
        // Insert feedback with the first available tool
        const { error: feedbackError } = await supabase
          .from('tool_feedback')
          .insert({
            tool_id: altToolData[0].id, // Access the id from the first element of the array
            rating: rating,
            is_helpful: rating >= 3,
            user_id: user.id
          });

        if (feedbackError) throw feedbackError;
      } else {
        // Insert feedback with the found tool
        const { error: feedbackError } = await supabase
          .from('tool_feedback')
          .insert({
            tool_id: toolData[0].id, // Access the id from the first element of the array
            rating: rating,
            is_helpful: rating >= 3,
            user_id: user.id
          });

        if (feedbackError) throw feedbackError;
      }

      // If there's a comment, save it
      if (comment.trim() && rating < 3) {
        const { error: commentError } = await supabase
          .from('user_messages')
          .insert({
            message: comment,
            tool_name: toolName,
            user_id: user.id,
            is_read: false
          });

        if (commentError) throw commentError;
      }

      toast({
        title: "Merci pour votre retour !",
        description: "Votre avis nous aide à améliorer nos outils.",
      });

      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi de votre retour.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-center">Que pensez-vous de cet outil ?</DialogTitle>
          <DialogDescription className="text-center">
            Votre avis nous aide à améliorer nos services
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-6">
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-gray-600">Notez cet outil de 1 à 5 étoiles</p>
            <StarRating 
              initialRating={rating} 
              onChange={setRating} 
              size="lg"
            />
          </div>

          {shouldShowCommentForm && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Pourriez-vous nous indiquer ce qui ne vous a pas convenu ?
              </p>
              <Textarea
                placeholder="Qu'est-ce qui pourrait être amélioré ?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isSubmitting}
                className="min-h-[100px]"
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!rating || isSubmitting}
            >
              {isSubmitting ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
